import { Container, Nav, Navbar, Dropdown, Image } from 'react-bootstrap';
import { useEffect, useRef, useState } from 'react';
import AmplifierLogoBlack from 'Assets/icons/Amplifier_Logo_Black.svg';
import { Typography } from 'Components/atoms';
import { ReactSVG } from 'react-svg';
import { FaUserCircle } from '@react-icons/all-files/fa/FaUserCircle';
import { FaKey } from '@react-icons/all-files/fa/FaKey';
import { MdOutlineKeyboardArrowDown } from '@react-icons/all-files/md/MdOutlineKeyboardArrowDown';
import SignOutIcon from 'Assets/icons/SignOutIcon.svg';
import { DEFAULT_WORKFLOW_ROUTE, SIGN_IN } from 'Core-utils/routes';
import { prefetchTagSpecsCatalog } from 'Hooks/useTagSpecsCatalog';
import { Link, useNavigate } from 'react-router-dom';
import { adminNavItems, viewerNavItems } from 'Core-utils/constants/constants';
import { NavLink } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { Dispatch, RootState } from 'Rdx/store';
import { useDispatch, useSelector } from 'react-redux';
import { clearSWRCache } from 'Core-utils/helpers/helpers';
import { clearTagSpecsCatalog } from 'Core-utils/helpers/tagSpecsCatalogCache';
import { AuthRole } from '@ampsec/platform-client';
import './style.scss';
import { useI18n } from 'Hooks/useI18n';
import { useEarlyAccess } from 'Hooks/useEarlyAccess';
import { useAskAmpyEnabled } from 'Hooks/useAskAmpyEnabled';
import { useAmpyChat } from 'Rdx/contexts/AmpyChatContext';

const AMPY_CHAT_OPEN_KEY = 'ampy_chat_sidebar_open';
interface ProfileComponentProps {
    onClickSignOutHandler: () => void;
}

interface SideNavProps {
    showAmpyChatToggle?: boolean;
}

const UserAvatar = ({ userImage }: { userImage?: string }) => {
    if (userImage) {
        return <Image src={userImage} roundedCircle={true} className="sideNav__user-avatar-element  ms-1 me-1" />;
    }
    return <FaUserCircle className="sideNav__user-avatar-element ms-1 me-1" />;
};

const ProfileComponent = ({ onClickSignOutHandler }: ProfileComponentProps) => {
    const { user, tenants } = useSelector((state: RootState) => state.auth);
    const { pictureUrl, email } = user;

    return (
        <Dropdown className="border border-structural-stroke-300 rounded sign-out mb-3" drop="up" data-testid="profile">
            <Dropdown.Toggle className="text-text-high-emphasis">
                <UserAvatar userImage={pictureUrl!} />
                <MdOutlineKeyboardArrowDown size={25} className="dropdown__dropdown-icon" data-testid="key-down-icon" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="bg-structural-CardBG p-0 border border-structural-stroke-300 overflow-hidden">
                <Dropdown.Item className="text-text-low-emphasis mt-1 mb-1" disabled>
                    <Typography variant="overline">{email}</Typography>
                </Dropdown.Item>
                <Dropdown.Divider className="bg-structural-stroke-500 m-0" />
                {tenants && tenants.length > 1 && (
                    <Dropdown.Item className="text-text-high-emphasis mt-1 mb-1">
                        <div className="text-text-medium-emphasis w-100 ps-0 d-flex align-items-center gap-2 border-0">
                            <FaKey />
                            <Link to="/auth/tenants" className="sideNav__link">
                                <Typography variant="body5">Change Tenant</Typography>
                            </Link>
                        </div>
                    </Dropdown.Item>
                )}
                <Dropdown.Item className="text-text-high-emphasis mt-1 mb-1" onClick={() => onClickSignOutHandler()}>
                    <div className="text-text-medium-emphasis w-100 ps-0 d-flex align-items-center gap-2 border-0">
                        <ReactSVG src={SignOutIcon} />
                        <div className="sideNav__link">
                            <Typography variant="body5">Sign Out</Typography>
                        </div>
                    </div>
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
};

const SparkleIcon = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z" fill="currentColor" />
    </svg>
);

const SideNav = ({ showAmpyChatToggle = false }: SideNavProps) => {
    const auth = useSelector((state: RootState) => state.auth);
    const selectedTenantId = auth.selectedTenant?.id as string | undefined;
    const dispatch = useDispatch<Dispatch>();
    const navigate = useNavigate();
    const { cache } = useSWRConfig();
    const { t } = useI18n();
    const { isEarlyAccessEnabled } = useEarlyAccess();
    const { isAskAmpyEnabled, isLoading: isAskAmpyLoading } = useAskAmpyEnabled();
    const { toggleOpen, isOpen } = useAmpyChat();
    const [isActivationBurst, setIsActivationBurst] = useState(false);
    const activationBurstTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (activationBurstTimeoutRef.current) {
                clearTimeout(activationBurstTimeoutRef.current);
            }
        };
    }, []);

    const onClickHandler = () => {
        clearSWRCache(cache);
        clearTagSpecsCatalog();
        try {
            window.localStorage.removeItem(AMPY_CHAT_OPEN_KEY);
        } catch {
            // ignore storage errors
        }
        dispatch.auth?.resetAuth();
        dispatch.ssoModel.setError(null);
        navigate(SIGN_IN);
    };

    const showEarlyAccessFeature = (auth.user.role === AuthRole.OWNER || auth.user.role === AuthRole.ADMIN) && isEarlyAccessEnabled;
    const onClickAmpyToggle = () => {
        const openingPanel = !isOpen;

        if (activationBurstTimeoutRef.current) {
            clearTimeout(activationBurstTimeoutRef.current);
        }

        if (openingPanel) {
            setIsActivationBurst(true);
            activationBurstTimeoutRef.current = setTimeout(() => {
                setIsActivationBurst(false);
            }, 420);
        } else {
            setIsActivationBurst(false);
        }

        toggleOpen();
    };

    const navItems = (
        [AuthRole.ADMIN, AuthRole.OWNER].includes(auth.user.role!)
            ? adminNavItems
            : viewerNavItems
        ).filter((navItem) => !navItem.earlyAccess || (navItem.earlyAccess && showEarlyAccessFeature));

    return (
        <div className="sideNav">
            <Navbar bg="dark" variant="dark" className="sideNav__navbar">
                <Container className="sideNav__container">
                    <Navbar.Brand className="sideNav__brand">
                        <ReactSVG src={AmplifierLogoBlack} className="brand-icon" />
                    </Navbar.Brand>
                    {showAmpyChatToggle && isAskAmpyEnabled && !isAskAmpyLoading && (
                        <button
                            type="button"
                            className={[
                                'sideNav__ampy-toggle',
                                isOpen ? 'sideNav__ampy-toggle--active' : '',
                                !isOpen ? 'sideNav__ampy-toggle--tease' : '',
                                isActivationBurst ? 'sideNav__ampy-toggle--activate-burst' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onClick={onClickAmpyToggle}
                            data-testid="side-nav-ampy-toggle"
                            title="Ask Ampy"
                        >
                            <span className="sideNav__ampy-toggle-icon">
                                <SparkleIcon size={18} />
                            </span>
                            <Typography variant="caption3" className="sideNav__ampy-toggle-label">
                                ASK AMPY
                            </Typography>
                        </button>
                    )}
                    <Nav className="sideNav__nav align-items-center">
                        {navItems.map(({ Icon, link, text }, index) => {
                            return (
                                <Nav.Link
                                    as={NavLink}
                                    to={link}
                                    key={index.toString()}
                                    className="sideNav__link"
                                    onClick={() => {
                                        if (link === DEFAULT_WORKFLOW_ROUTE && selectedTenantId) {
                                            void prefetchTagSpecsCatalog(selectedTenantId, true);
                                        }
                                    }}
                                >
                                    {typeof Icon === 'string' ? (
                                        <ReactSVG src={Icon} className="sideNav__svg-icon" />
                                    ) : (
                                        <div className="sideNav__react-icon">{Icon}</div>
                                    )}
                                    <Typography variant="caption3">{t(text).toUpperCase()}</Typography>
                                </Nav.Link>
                            );
                        })}
                    </Nav>
                    <ProfileComponent onClickSignOutHandler={onClickHandler} />
                </Container>
            </Navbar>
        </div>
    );
};

export default SideNav;
